import { useState, useEffect, useRef, createElement, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { Paper, Center, Text, Loader,Stack, Button, Title, Group, Container, Flex} from '@mantine/core';
import { IconArrowLeft,IconCircleDashedX } from '@tabler/icons-react';
import { Row, Col } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion'; //old

import { setActiveStep, setStepId, SavePreRegistration,GetKnownUser,setCheckoutSummary } from '../actions/ResponsesActions';
import { GetCarreras } from '../actions/CarreraActions';

import Preloader from '../components/Preloader';
import { notifyError } from '../components/Toast';
import BonusesInfoModal from '../components/BonusesInfoModal';
import SideStepper from '../components/SideStepper';
import TextPriceWithDiscount from '../components/TextPriceWithDiscount';
import {stepIds} from "../enums/fieldsDataConfig";
import { useStepResponses } from '../hooks/useStepResponses';
import useFormTracking from '../hooks/useFormTracking';
import { getStepsConfig } from '../config/stepsConfig';
import typeConfig from '../config/typeConfig';
import parseNumber from "../utils/parseNumber";


const CheckoutPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formRefs = useRef({});
  const location = useLocation();

  const {data: dataCarreras} = useSelector((state) => state.carreras.carreras);
  const {data: dataCampus} = useSelector((state) => state.carreras.campus);
  const {data: dataTurnos} = useSelector((state) => state.carreras.turnos);
  const {data: valorCarrera} = useSelector((state) => state.carreras.valor);
  const {activeStep,steps:stepResponses,bonus} = useSelector((state) => state.respuestas);
  const knownUser = useSelector((state) => state.respuestas.knownUser);
  const summary = useSelector((state) => state.respuestas.checkoutSummary);

  const ProgramResponse = useStepResponses(stepIds.PROGRAMA);
  const ConfirmationResponse = useStepResponses(stepIds.CONFIRMACION);
  const [knownUserLoaded, setKnownUserLoaded] = useState(false);
  const [highestStepVisited, setHighestStepVisited] = useState(activeStep);
  const [bonusesOpened, setBonusesOpened] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [steps, setSteps] = useState([]);

  const { track, resetStepTimer } = useFormTracking(activeStep);
  const isInitialStepRender = useRef(true);

  const [finishStatus, setFinishStatus] = useState({
    finishInit: false,
    isLoading: false,
    saveSuccess: null,
  });

  // Estado para preservar el precio normal de la primera llamada API
  const [precioNormalGuardado, setPrecioNormalGuardado] = useState(null);



  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      t: params.get("t")?.toLowerCase() || 'g',
      c: params.get("c")?.trim() || null,
      p: params.get("p")?.trim() || null,
      fi: params.get("fi")?.toLowerCase(),
      token: params.get("token")?.toLowerCase(),
      em:params.get("em"),
    };
  }, [location.search]);

  const handleUrlParams = useCallback(() => {
    if (queryParams.token) {
      const timeout = () => setTimeout(() => {
        setKnownUserLoaded(true);
      }, 5000);
  
      dispatch(GetKnownUser(queryParams.token, queryParams.t)).then(() => {
        timeout()
      });
    }
    
    if (queryParams.c && queryParams.p && queryParams.fi) {

      const fechaIngreso = new Date(queryParams.fi + 'T00:00:00')
      const mes = fechaIngreso.getMonth() + 1;
      const periodo = mes >= 1 && mes <= 7 ? 0 : 1;

      dispatch(GetCarreras(fechaIngreso,periodo,queryParams.t));
    }
    
  }, [queryParams, dispatch]);

  const stepsConfig = useMemo(() => getStepsConfig(queryParams, typeConfig), [queryParams, typeConfig]);

  const initializeSteps = () => {
    const steps = stepsConfig
      .filter(step => step.isEnable)
      .map((step, index) => {
        const stepNumber = index + 1;
        dispatch(setStepId(stepNumber, step.id));
        return {
          number: stepNumber,
          ...step,
          form: createElement(step.form, {
            key: stepNumber,
            ref: (el) => (formRefs.current[stepNumber] = el),
            stepNumber: stepNumber,
            queryParams: queryParams,
            id: step.id,
          }),
        };
      });
    setSteps(steps);
  };

  const initializeWindowSize = () => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 700);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }

  useEffect(() => {
    initializeSteps();
    initializeWindowSize();
    track('form_started');
  }, []);

  useEffect(() => {
    if (isInitialStepRender.current) {
      isInitialStepRender.current = false;
      return;
    }
    track('step_changed', { fieldName: `step_${activeStep + 1}` });
    resetStepTimer();
  }, [activeStep]);

  function getPreciosYDescuentos({ t, valorCarrera }) {
    if (!valorCarrera || !valorCarrera[0]) {
      
      console.warn('No se encontraron valores de carrera para calcular precios y descuentos');
      return {
        precioOriginal: 0,
        precioDescuento: 0,
        precioComunidad: 0,
        descuentoPorcentaje: 0,
        descuentoVencimiento: null,
      };
    }

    const parse = (v) => parseNumber(v);
    let precioOriginal = 0, precioDescuento = 0, precioComunidad = 0, descuentoPorcentaje = 0;

    const v = valorCarrera[0];
    const descuentoVencimiento = v.dt_bonif_matr_hasta || null;
    const isComunidad = knownUser.comunity;


    // Estructura unificada para carreras y microcredenciales
    precioOriginal = parse(v.f_valor_matricula);        // Precio original
    
    if (isComunidad) {
      // Orden invariante: tomamos el valor actual y el guardado (si existe)
      const valorActual = parse(v.f_matric_con_desc);
      const valorGuardado = precioNormalGuardado ?? valorActual;

      // El precio de comunidad siempre debe ser el menor, y el normal el mayor
      precioComunidad = Math.min(valorActual, valorGuardado);
      precioDescuento = Math.max(valorActual, valorGuardado);

    } else {
      // Si no es miembro UAI, solo precio normal
      precioDescuento = parse(v.f_matric_con_desc);
      precioComunidad = precioDescuento;
      setPrecioNormalGuardado(null); // Limpiar precio guardado
    }
    
    descuentoPorcentaje = parse(v.f_porc_bonif_matricula); // porcentaje del back

    return { precioOriginal, precioDescuento, precioComunidad, descuentoPorcentaje, descuentoVencimiento };
  }

  useEffect(() => {
    if (
      !dataCarreras || !dataCampus || !dataTurnos || !valorCarrera || valorCarrera.length === 0 ||
      !ProgramResponse.carrera || !ProgramResponse.campus || !ProgramResponse.turno || !ProgramResponse.fechaIngreso
    ) return;

    const careerDetail = dataCarreras.find((f) => f.careers.find((c) => c.cod === ProgramResponse.carrera))?.careers.find((c) => c.cod === ProgramResponse.carrera)?.description;
    const campus =  dataCampus.find((c) => c.c_campus_id === ProgramResponse.campus)?.vc_descripcion;
    const turno = dataTurnos.find((t) => t.c_turno_id === ProgramResponse.turno)?.vc_descripcion;
    const fechaIngreso = ProgramResponse.fechaIngreso;

    const {
      precioOriginal,
      precioDescuento,
      precioComunidad,
      descuentoPorcentaje,
      descuentoVencimiento
    } = getPreciosYDescuentos({ t: queryParams.t, valorCarrera, knownUser });

    dispatch(setCheckoutSummary({
      carrera: careerDetail,
      fechaIngreso,
      campus,
      turno,
      precioOriginal,  
      precioDescuento,
      precioComunidad,
      descuentoVencimiento,
      descuentoPorcentaje,
    }));

  }, [dataCarreras, dataCampus, dataTurnos, valorCarrera, knownUser]);

  // Guardar SOLO una vez el precio normal (1ra llamada: tipoBonif = null) para miembros UAI
  useEffect(() => {
    if (!knownUser.comunity) return;
    if (!valorCarrera || !valorCarrera[0]) return;

    // Si aún no capturamos el precio normal, tomamos el de la primera respuesta
    if (precioNormalGuardado === null) {
      const v = valorCarrera[0];
      const precioNormal = parseNumber(v.f_matric_con_desc);
      setPrecioNormalGuardado(precioNormal);
    }
  }, [valorCarrera, knownUser.comunity, precioNormalGuardado]);

  // Limpiar precio guardado cuando no es miembro UAI
  useEffect(() => {
    if (!knownUser.comunity) {
      setPrecioNormalGuardado(null);
    }
  }, [knownUser.comunity]);


  useEffect(() => {
    handleUrlParams();
  }, [handleUrlParams]);

  const handleSavePreRegistration = async () => {
    track('form_submitted');
    setFinishStatus((prev) => ({...prev,finishInit: true, isLoading: true}));
    try {
      await dispatch(SavePreRegistration(stepResponses,queryParams.t,navigate));
      track('form_completed');
    } catch (error) {
      track('form_submission_failed');
      setFinishStatus((prev) => ({...prev,saveSuccess: false}));

      setTimeout(() => {
        handleStepChange(activeStep)
        setFinishStatus((prev) => ({...prev,finishInit: false}));
      }, 8000); 
      
    } finally {
      setFinishStatus((prev) => ({...prev,isLoading: false}));
    }
  };

  const handleBonusesButtonClick = () => {
    setBonusesOpened(true);
  };

  const handleStepChange = async (nextStep) => {
    const isOutOfBounds = nextStep > steps.length || nextStep < 0;
    if (isOutOfBounds) return;
  
    const isForward = nextStep > activeStep;
  
    if (isForward) {
      const currentFormRef = formRefs.current[activeStep + 1 ];

      const isValid = await currentFormRef.validate();
      if (isValid) {
        dispatch(setActiveStep(nextStep));
        setHighestStepVisited((hSC) => Math.max(hSC, nextStep));

        if(nextStep === steps.length){
          setFinishStatus((prev) => ({...prev,finishInit: true}));
          handleSavePreRegistration()
        }

      } else {
        track('step_validation_error', { fieldName: `step_${activeStep + 1}` });
        notifyError('Complete todos los campos requeridos');
      }

    } else {
      dispatch(setActiveStep(nextStep));
      setHighestStepVisited((hSC) => Math.max(hSC, nextStep));
    }
  };
  
  const shouldAllowSelectStep = (step) => highestStepVisited >= step && activeStep !== step;

  if(!knownUserLoaded && !knownUser.loading && queryParams.token){
    return (
      <Preloader loaderMessage='Estamos cargando su informacion ...' />
    )
  }

  return (
    <div className='title' style={{ 
      backgroundColor: 'rgb(249, 250, 251)',
      overflowX:'hidden',
      width:'100%',
      }}
    >
      <div className='steps-section'>
        <div className='side-progress fixed'>
          <div className='side-progress-content'>
            <SideStepper
              steps={steps}
              activeStep={activeStep}
              onStepClick={(step) => handleStepChange(step)}
              isMobile={isMobile}
              shouldAllowSelectStep={shouldAllowSelectStep}
            />
          </div>
        </div>
        <div className={`form form-fixed`}>
          {knownUser.loading ? (
            <Center h={'100vh'}>
              <Loader color="#f20a61" size="lg" type="bars" />
            </Center>
          ):(
            <>
              <div className='program-section'>
                <Row className="g-3">
                  <Col md={12} lg={8}>
                    <div>
                      <Title order={2} fw={300} mb='xs'>
                        Solicitud de Preinscripción a <Text span inherit fw={500} c={"#f20a61"}> {typeConfig[queryParams.t].description} </Text>
                      </Title>
                      { !isMobile && (
                        <Text size='lg'>
                          Completa este formulario seleccionando la carrera, curso o diplomatura de tu interés. 
                          Revisa las fechas y opciones para encontrar la que mejor se alinee con tu plan.
                        </Text>
                      )}
                    </div>
                  </Col>
                  <Col md={12} lg={4}>
                    <Center h={'100%'}>
                      <Button
                        variant="filled"
                        color="#f20a61"
                        onClick= {handleBonusesButtonClick}
                        size='md'
                        fullWidth ={isMobile}
                      >
                        Ver bonificaciones
                      </Button>
                    </Center>
                  </Col>
                </Row>
              </div>
              {valorCarrera && activeStep != 0 && activeStep != (steps.length - 1) && (
                <Paper 
                  shadow="md" 
                  radius="md" 
                  withBorder 
                  padding="xl" 
                  w='95%'
                  style={{ border: '2px solid rgb(242, 10, 95)'}}
                >
                  <div>
                    <Row className="py-3">
                    {queryParams.t !== "m" ? (
                    <>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          {typeConfig[queryParams.t]?.description}
                        </Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.carrera}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Campus</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.campus}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Turno</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.turno}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          Valor de la matrícula
                        </Text>
                        <TextPriceWithDiscount
                          size='lg'
                          fontWeight={500}
                          price={summary.precioOriginal || 0}
                          discountedPrice={summary.precioDescuento || 0}
                          queryparams={queryParams}
                          priceComunityUAI={summary.precioComunidad || 0}
                        />
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          {typeConfig[queryParams.t]?.description}
                        </Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.carrera}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Campus</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.campus}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>Turno</Text>
                        <Text size='lg' fw={500} c={'#333'}>
                          {summary.turno}
                        </Text>
                      </Col>
                      <Col md={3} style={{ textAlign: 'center' }}>
                        <Text fw={600} c={'rgb(242, 10, 95)'}>
                          Valor de la microcredencial
                        </Text>
                      
                        <TextPriceWithDiscount
                          size='lg'
                          fontWeight={500}
                          queryparams={queryParams}
                          price={summary.precioOriginal || 0}
                          discountedPrice={summary.precioDescuento || 0}
                          priceComunityUAI={summary.precioComunidad || 0}
                        />
                      </Col>
                    </>
                  )}
                    </Row>
                  </div>
                </Paper>
              )}
              <div className='form-header'>
                <Stack>
                  <Text ta={'center'} size="35px" weight={900} mb={10}>
                    {steps[activeStep]?.title}
                  </Text>
                  <Text ta={'center'} size="md" c="dimmed">
                    {steps[activeStep]?.description}
                  </Text>
                </Stack>
              </div>
              <div className='form-content' 
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <AnimatePresence mode='wait'>
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ marginBottom: '1rem', padding: '1rem' }}
                  >
                    {finishStatus.finishInit  ? (
                      <>
                        <Container h={'40vh'}>
                          <Flex
                            h={'100%'}
                            gap="md"
                            justify="center"
                            align="center"
                            direction="column"
                            wrap="wrap"
                          >
                            {finishStatus.isLoading ? (
                              <Center>
                                <Stack align="center" justify="center" gap={'sm'}>
                                  <Loader color="pink" size="lg" type="dots" />
                                  <Text mt="sm" ta={'center'}>Estamos procesando su preinscripción</Text>
                                </Stack>
                              </Center>
                            ) : finishStatus.saveSuccess === false ? (
                              <Center>
                                <Stack align="center" justify="center" gap={'sm'}>
                                  <IconCircleDashedX size={50} color="red" />
                                  <Text mt="sm">Ocurrió un error, pronto sera redirigido al formulario ...</Text>
                                </Stack>
                              </Center>
                            ) : null}

                          </Flex>
                        </Container>
                      </>
                    ) : 
                      steps.find(step => step.number === activeStep + 1)?.form
                    }
                  </motion.div>
                </AnimatePresence>
                <div style={{ 
                  padding: '1rem 2rem',
                  borderTop: '2px solid rgba(242, 10, 95, 0.57)',
                }}>
                  <Group justify="flex-end" wrap='nowrap'>
                    {!finishStatus.finishInit && (
                      <>
                        {activeStep === 0 && (
                          <Button
                            variant="filled"
                            color="#f20a61"
                            fullWidth={isMobile}
                            onClick={() => handleStepChange(activeStep + 1)}
                          >
                            {isMobile ? "Continuar" : "Continuar con la preinscripción"}
                          </Button>
                        )}

                        {activeStep > 0 && activeStep < steps.length - 1 && (
                          <>
                            <Button
                              variant="filled"
                              color="#C1C1C1"
                              onClick={() => handleStepChange(activeStep - 1)}
                            >
                              {isMobile ? <IconArrowLeft /> : "Anterior"}
                            </Button>
                            <Button
                              variant="filled"
                              color="#f20a61"
                              fullWidth={isMobile}
                              onClick={() => handleStepChange(activeStep + 1)}
                            >
                              Siguiente
                            </Button>
                          </>
                        )}

                        {activeStep === steps.length - 1 && (
                          <Button
                            variant="filled"
                            color="#f20a61"
                            miw="30%"
                            fullWidth={isMobile}
                            disabled={
                              !ConfirmationResponse?.aceptaCondiciones || 
                              (queryParams.t === 'm' && !ConfirmationResponse?.aceptaRequerimientos)
                            }
                            onClick={() => handleStepChange(activeStep + 1)}
                          >
                            Confirmar Preinscripción
                          </Button>
                        )}
                      </>
                    )}
                  </Group>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <BonusesInfoModal
        isOpened={bonusesOpened}
        onClose={() => setBonusesOpened(false)}
      />
    </div>
  );
};

export default CheckoutPage;